import { Trans, useLingui } from "@lingui/react/macro";
import { Button, Dialog, DialogClose, DialogContent, DialogTitle, Spinner } from "@rakazo/ui-web";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import type { SpaceQueries } from "../../lib/query";

export type BotRunErrorTarget = {
  runId: string;
  botId: string;
  spaceId: string;
  botName: string;
  error: string | null;
};

export function BotRunErrorDialog({
  target,
  queries,
  onClose,
  onDismiss,
}: {
  target: BotRunErrorTarget;
  queries: SpaceQueries;
  onClose: () => void;
  onDismiss?: () => void;
}) {
  const { t, i18n } = useLingui();
  const failure = useQuery({
    ...queries.runs.failure.queryOptions({ input: { botId: target.botId, runId: target.runId } }),
    retry: false,
  });
  const data = failure.data;
  const error = data?.diagnostic?.summary ?? data?.error ?? target.error;
  const unknownCause = !error || error.trim() === "Internal server error";
  const when = data?.failedAt ?? data?.startedAt ?? data?.createdAt;
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        data-testid="bot-run-error-dialog"
        className="app-no-drag max-h-[85dvh] overflow-y-auto sm:max-w-xl"
      >
        <div className="flex items-center justify-between gap-3">
          <DialogTitle className="min-w-0 break-words text-base">{t`${target.botName} · Error`}</DialogTitle>
          <DialogClose render={<Button variant="ghost" size="icon-sm" />} aria-label={t`Close`}>
            <X size={16} aria-hidden="true" />
          </DialogClose>
        </div>
        <p
          className="whitespace-pre-wrap break-words text-sm text-destructive"
          data-testid="bot-run-error-message"
        >
          {unknownCause ? t`This run stopped, but its cause was not recorded.` : error}
        </p>
        {failure.isPending ? <Spinner aria-label={t`Loading failure details…`} /> : null}
        {failure.isError ? (
          <div
            role="alert"
            className="flex items-center justify-between gap-3 text-sm text-destructive"
          >
            <Trans>Could not load the task details.</Trans>
            <Button variant="ghost" size="sm" onClick={() => void failure.refetch()}>
              <Trans>Try again</Trans>
            </Button>
          </div>
        ) : null}
        {data ? (
          <>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              {when ? (
                <>
                  <dt className="text-muted-foreground">
                    <Trans>Failed</Trans>
                  </dt>
                  <dd>
                    <time dateTime={when}>
                      {new Date(when).toLocaleString(i18n.locale || "en", {
                        dateStyle: "medium",
                        timeStyle: "medium",
                      })}
                    </time>
                  </dd>
                </>
              ) : null}
              <dt className="text-muted-foreground">
                <Trans>Task</Trans>
              </dt>
              <dd className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words">
                {data.task}
              </dd>
              {data.model ? (
                <>
                  <dt className="text-muted-foreground">
                    <Trans>Model</Trans>
                  </dt>
                  <dd className="break-words">
                    {[data.provider, data.model].filter(Boolean).join(" · ")}
                  </dd>
                </>
              ) : null}
              {data.lastAction ? (
                <>
                  <dt className="text-muted-foreground">
                    <Trans>Last recorded action</Trans>
                  </dt>
                  <dd>
                    {data.lastAction.label} ·{" "}
                    {data.lastAction.outcome === "error"
                      ? t`Failed`
                      : data.lastAction.outcome === "paused"
                        ? t`Waiting`
                        : t`Done`}
                  </dd>
                </>
              ) : null}
            </dl>
            <details className="text-sm">
              <summary className="w-fit cursor-pointer text-muted-foreground">
                <Trans>Technical details</Trans>
              </summary>
              <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-xs">
                <dt>
                  <Trans>Run</Trans>
                </dt>
                <dd className="break-all">{data.runId}</dd>
                <dt>
                  <Trans>Recorded error</Trans>
                </dt>
                <dd className="whitespace-pre-wrap break-words">{data.error ?? t`Unavailable`}</dd>
                {data.lastAction?.error ? (
                  <>
                    <dt>
                      <Trans>Last action error</Trans>
                    </dt>
                    <dd className="whitespace-pre-wrap break-words">{data.lastAction.error}</dd>
                  </>
                ) : null}
                {data.diagnostic ? (
                  <>
                    <dt>
                      <Trans>Error type</Trans>
                    </dt>
                    <dd>{data.diagnostic.errorType}</dd>
                    {data.diagnostic.code ? (
                      <>
                        <dt>
                          <Trans>Code</Trans>
                        </dt>
                        <dd>{data.diagnostic.code}</dd>
                      </>
                    ) : null}
                    {data.diagnostic.status ? (
                      <>
                        <dt>HTTP</dt>
                        <dd>{data.diagnostic.status}</dd>
                      </>
                    ) : null}
                    {data.diagnostic.timeoutMs !== null ? (
                      <>
                        <dt>
                          <Trans>Timeout</Trans>
                        </dt>
                        <dd>{data.diagnostic.timeoutMs} ms</dd>
                      </>
                    ) : null}
                    {data.diagnostic.elapsedMs !== null ? (
                      <>
                        <dt>
                          <Trans>Elapsed</Trans>
                        </dt>
                        <dd>{data.diagnostic.elapsedMs} ms</dd>
                      </>
                    ) : null}
                  </>
                ) : null}
              </dl>
            </details>
          </>
        ) : null}
        {onDismiss ? (
          <div className="flex justify-end">
            <Button onClick={onDismiss}>
              <Trans>Dismiss error</Trans>
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
